import React, { useState, useEffect, useRef } from 'react';
import { Table, Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloud } from '@fortawesome/free-solid-svg-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, Timestamp, updateDoc, doc, setDoc, getDoc, getDocs, where, writeBatch, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import swal from 'sweetalert';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { buildCourseKey, buildInvoicesForCourse, normalizeCourseName } from './invoiceSchedule';
import { buildAdmissionConfirmationEmail, buildAdmissionFeeReceiptEmail, buildReceiptEmail } from '../utils/emailTemplates';
import { canContinueTuitionWithoutAdmissionFee, hasAdmissionFeeExemptStudentStatus } from '../Components/useAdmissionJourney';
import {
  DEFAULT_USD_TO_KES_RATE,
  fetchUsdToKesRate,
  formatMoney,
  formatMoneyFromKes,
  getCurrencyConversionRate,
  getDisplayAmountFromKes,
  getPaystackMinorAmountFromKes,
  resolveStudentCurrency,
} from '../utils/currency';

const DEFAULT_ADMISSION_FEE_AMOUNT_KES = 2000;
const MIN_PARTIAL_INVOICE_PAYMENT_KES = 1000;
const MIN_PARTIAL_INVOICE_PERCENT = 0.8;
const ADMISSION_FEE_SETTINGS_DOC = 'admissionFee';
const ADMISSION_MPESA_PAYBILL = '4052081';
const STUDENT_PAYMENT_API_BASE_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/sendMoneyApi';
const PAYSTACK_PUBLIC_KEY = 'pk_live_f75e7fc5c652583410d16789fc9955853373fc8c';
const sanitizePaymentReference = (reference) => String(reference || '').replace(/[^A-Za-z0-9_-]/g, '_');
const buildPaystackPaymentDocId = (reference) => `paystack_${sanitizePaymentReference(reference || Date.now())}`;
const resolvePaystackSourceCurrency = (currency) => (String(currency || '').toUpperCase() === 'USD' ? 'USD' : 'KES');

function InvoicesComp() {
  // States
  const [courseDetails, setCourseDetails] = useState({
    feeDeposit: 0,
    feeInstallments: 0,
    mcoursePrice: 0,
    mcourseDuration: 0,
  });
  const [amountPaid, setAmountPaid] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [amountPaidThisMonth, setAmountPaidThisMonth] = useState(0);
  const [totalRemainingBalance, setTotalRemainingBalance] = useState(0); // New state for total remaining balance
  const [theCourseName, setThecourseName] = useState("");
  const [studentStatus, setstudentStatus] = useState("");
  const [thecohort, setCohort] = useState("");
  const [programDuration, settheDuration] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const [bio, setBio] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [thePhoneNumber, setPhoneNumber] = useState("");
  const [userId, setUserId] = useState(null);
  const [userDocId, setUserDocId] = useState("");
  const [secondaryCourses, setSecondaryCourses] = useState([]);
  const [secondaryCourseContext, setSecondaryCourseContext] = useState(null);
  const [isSecondaryPayment, setIsSecondaryPayment] = useState(false);
  const [courseTabs, setCourseTabs] = useState([]);
  const [activeCourseKey, setActiveCourseKey] = useState("");
  const [courseCatalog, setCourseCatalog] = useState({});
  const [paymentMap, setPaymentMap] = useState({});
  const [studentRecord, setStudentRecord] = useState({});
  const [showAdmissionFeeModal, setShowAdmissionFeeModal] = useState(false);
  const [isAdmissionFeeLoading, setIsAdmissionFeeLoading] = useState(false);
  const [admissionFeeStatusText, setAdmissionFeeStatusText] = useState('');

  const auth = getAuth();
  const [amountToPay, setAmountToPay] = useState(0); // Amount to be paid
  const [selectedMonth, setSelectedMonth] = useState(""); // Selected month for payment
  const [selectedPaymentType, setSelectedPaymentType] = useState("invoice");
  const [selectedInvoiceExpectedAmount, setSelectedInvoiceExpectedAmount] = useState(0);
  const [selectedInvoiceBalanceAmount, setSelectedInvoiceBalanceAmount] = useState(0);
  const [customPaymentAmount, setCustomPaymentAmount] = useState("");
  const mpesaNumber = useRef();
  const admissionMpesaNumber = useRef();
  const admissionFeeCardRef = useRef(null);
  const paymentPollRef = useRef(null);
  const admissionPaymentPollRef = useRef(null);

  const [error, setError] = useState('');
  const [paymentStatusText, setPaymentStatusText] = useState("");

  //
  const routerLocation = useRouterLocation();
  const secondaryFromRoute = routerLocation?.state?.secondaryCourse || null;
  const [loggedInUserIpAddress, setloggedInUserIpAddress] = useState('');
  const [studentCurrencyCode, setStudentCurrencyCode] = useState('KES');
  const [usdToKesRate, setUsdToKesRate] = useState(DEFAULT_USD_TO_KES_RATE);
  const [admissionFeeAmountKes, setAdmissionFeeAmountKes] = useState(DEFAULT_ADMISSION_FEE_AMOUNT_KES);
  const currentCurrencyCode = studentCurrencyCode === 'USD' ? 'USD' : 'KES';
  const conversionRate = getCurrencyConversionRate(currentCurrencyCode, usdToKesRate);
  const isKenyanStudent = currentCurrencyCode === 'KES';
  const isAdmissionFeePaid = (studentRecord.admissionFeeStatus || '').toString().toLowerCase() === 'paid';
  const isAdmissionFeeExempt = hasAdmissionFeeExemptStudentStatus(studentRecord);
  const prospectAdmissionNumber = (studentRecord.prospectAdmissionNumber || '').toString().trim();
  const hasProspectAdmissionNumber = Boolean(prospectAdmissionNumber);
  const shouldShowAdmissionFeeCard = !isPageLoading && studentStatus === 'applicant' && !isAdmissionFeeExempt;
  const shouldRequireAdmissionFeeBeforeTuition = shouldShowAdmissionFeeCard && !isAdmissionFeePaid;
  const storedAdmissionFeeAmountPaid = Number(studentRecord.admissionFeeAmountPaid || 0);
  const storedAdmissionFeeBalance = Number(studentRecord.admissionFeeBalance);
  const admissionFeeAmountPaidKes = Math.max(0, Number.isFinite(storedAdmissionFeeAmountPaid) ? storedAdmissionFeeAmountPaid : 0);
  const admissionFeeBalanceKes = Math.max(
    0,
    Number.isFinite(storedAdmissionFeeBalance)
      ? storedAdmissionFeeBalance
      : Math.max(0, admissionFeeAmountKes - admissionFeeAmountPaidKes)
  );
  const admissionFeePromptAmountKes = isAdmissionFeePaid
    ? 0
    : (admissionFeeBalanceKes > 0 ? admissionFeeBalanceKes : admissionFeeAmountKes);
  const admissionFeeDisplayAmount = formatMoneyFromKes(admissionFeeAmountKes, currentCurrencyCode, usdToKesRate);
  const admissionFeePaidDisplayAmount = formatMoneyFromKes(admissionFeeAmountPaidKes, currentCurrencyCode, usdToKesRate);
  const admissionFeeRemainingDisplayAmount = formatMoneyFromKes(admissionFeePromptAmountKes, currentCurrencyCode, usdToKesRate);
  const hasPartialAdmissionFeePayment = !isAdmissionFeePaid && admissionFeeAmountPaidKes > 0;
  const admissionCardTitle = isAdmissionFeePaid
    ? 'Admission fee received'
    : hasPartialAdmissionFeePayment
      ? 'Admission fee top-up required'
    : hasProspectAdmissionNumber
      ? 'Admission fee required'
      : 'Admission review in progress';
  const admissionCardDescription = isAdmissionFeePaid
    ? 'Your admission fee has been recorded. Our admissions team will contact you with interview scheduling and the next admission steps.'
    : hasPartialAdmissionFeePayment
      ? `We have recorded ${admissionFeePaidDisplayAmount}. Please top up ${admissionFeeRemainingDisplayAmount} so our admissions team can move your application to the next stage.`
    : hasProspectAdmissionNumber
      ? 'Please complete your admission fee so our admissions team can move your application to the next stage.'
      : 'Our admissions team is reviewing your application and preparing your admission reference. Please check again shortly.';
  const admissionCardMetaLabel = isAdmissionFeePaid ? 'Status' : hasProspectAdmissionNumber ? 'Balance due' : 'Status';
  const admissionCardMetaValue = isAdmissionFeePaid ? 'Paid' : hasProspectAdmissionNumber ? admissionFeeRemainingDisplayAmount : 'Preparing';
  const admissionCardMetaNote = isAdmissionFeePaid
    ? `Total received: ${admissionFeePaidDisplayAmount}${studentRecord.admissionFeePaymentReference ? ` • Ref: ${studentRecord.admissionFeePaymentReference}` : ''}`
    : hasProspectAdmissionNumber
      ? `Paid so far: ${admissionFeePaidDisplayAmount} • Application Ref: ${prospectAdmissionNumber}`
      : 'Your admission reference will appear here once it is ready.';

  const convertKesToDisplayAmount = (amountInKes) => getDisplayAmountFromKes(amountInKes, currentCurrencyCode, usdToKesRate);
  const convertDisplayAmountToKes = (displayAmount) => {
    const numericAmount = Number(displayAmount || 0);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return 0;
    return currentCurrencyCode === 'USD'
      ? Math.round(numericAmount * usdToKesRate)
      : Math.round(numericAmount);
  };
  const formatDisplayInputAmount = (amountInKes) => {
    const displayAmount = convertKesToDisplayAmount(amountInKes);
    return currentCurrencyCode === 'USD'
      ? displayAmount.toFixed(2)
      : String(Math.round(displayAmount));
  };
  const getInvoicePaymentBounds = (expectedAmountKes, remainingAmountKes, totalRemainingAmountKes) => {
    const safeExpectedAmount = Math.max(0, Number(expectedAmountKes) || 0);
    const safeRemainingAmount = Math.max(0, Number(remainingAmountKes) || 0);
    const safeTotalRemainingAmount = Math.max(0, Number(totalRemainingAmountKes) || 0);
    const calculatedMinimum = Math.max(
      MIN_PARTIAL_INVOICE_PAYMENT_KES,
      Math.ceil(safeExpectedAmount * MIN_PARTIAL_INVOICE_PERCENT)
    );

    return {
      minKes: safeRemainingAmount > 0 ? Math.min(safeRemainingAmount, calculatedMinimum) : 0,
      maxKes: safeTotalRemainingAmount
    };
  };
  const invoicePaymentBounds = getInvoicePaymentBounds(selectedInvoiceExpectedAmount, selectedInvoiceBalanceAmount, totalRemainingBalance);
  const minimumInvoicePaymentLabel = formatMoney(convertKesToDisplayAmount(invoicePaymentBounds.minKes), currentCurrencyCode);
  const maximumInvoicePaymentLabel = formatMoney(convertKesToDisplayAmount(invoicePaymentBounds.maxKes), currentCurrencyCode);


  //generate user token
  const [token, setToken] = useState();
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken().then(setToken).catch(err => console.error(err));
      }
    });
    return () => unsubscribe();
  }, []);
  //end


  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => setloggedInUserIpAddress(data.ip))
      .catch(error => console.error('Error fetching IP address:', error));
  }, []); // The empty array ensures this effect runs only once, on component mount.

  useEffect(() => {
    let isMounted = true;

    const fetchFinanceSettings = async () => {
      try {
        const [rate, admissionFeeSnap] = await Promise.all([
          fetchUsdToKesRate(db),
          getDoc(doc(db, 'Settings', ADMISSION_FEE_SETTINGS_DOC))
        ]);
        const admissionFeeAmount = Number(admissionFeeSnap.data()?.amountKes || DEFAULT_ADMISSION_FEE_AMOUNT_KES);

        if (isMounted) {
          setUsdToKesRate(rate);
          setAdmissionFeeAmountKes(
            Number.isFinite(admissionFeeAmount) && admissionFeeAmount > 0
              ? admissionFeeAmount
              : DEFAULT_ADMISSION_FEE_AMOUNT_KES
          );
        }
      } catch (error) {
        console.error('Failed to load finance settings:', error);
      }
    };

    fetchFinanceSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  // Handle Make Payment
  const handleMakePayment = (expectedAmount, paidAmount, month) => {
    const balance = expectedAmount - paidAmount; // Calculate remaining balance
    setSelectedPaymentType("invoice");
    setSelectedInvoiceExpectedAmount(expectedAmount);
    setSelectedInvoiceBalanceAmount(balance);
    setAmountToPay(balance); // Set the balance as the amount to pay
    setCustomPaymentAmount(formatDisplayInputAmount(balance));
    setSelectedMonth(month); // Set the month for which the payment is being made
    //processPayment(balance);
  };

  const handleProceedToPayment = () => {
    let amountInKes = Number(amountToPay || 0);

    if (selectedPaymentType === "invoice") {
      const parsedDisplayAmount = Number(customPaymentAmount || 0);
      if (!Number.isFinite(parsedDisplayAmount) || parsedDisplayAmount <= 0) {
        swal('Payment Error', 'Please enter a valid amount to pay.', 'error');
        return;
      }

      amountInKes = convertDisplayAmountToKes(parsedDisplayAmount);
      if (!amountInKes || amountInKes <= 0) {
        swal('Payment Error', 'Please enter a valid amount to pay.', 'error');
        return;
      }

      if (amountInKes < invoicePaymentBounds.minKes) {
        swal(
          'Payment Error',
          `The minimum payment for ${selectedMonth} is ${minimumInvoicePaymentLabel}.`,
          'error'
        );
        return;
      }

      if (amountInKes > invoicePaymentBounds.maxKes) {
        swal(
          'Payment Error',
          `The maximum payment for ${selectedMonth} is ${maximumInvoicePaymentLabel}.`,
          'error'
        );
        return;
      }
    }

    if (!amountInKes || amountInKes <= 0) {
      swal('Payment Error', 'Please select a valid invoice amount.', 'error');
      return;
    }

    toast.info(`Initiating payment for ${selectedMonth}`);
    setIsLoading(true);
    setPaymentStatusText(isKenyanStudent ? 'Sending payment prompt to your phone...' : 'Opening secure Paystack checkout...');

    const displayAmount = getDisplayAmountFromKes(amountInKes, currentCurrencyCode, usdToKesRate);
    const finalAmountKes = amountInKes;
    const intakeSuffix = secondaryCourseContext?.intakeDate ? ` (${secondaryCourseContext.intakeDate})` : '';
    const paymentDesc = `Payment for ${theCourseName}${intakeSuffix} Course`;

    if (isKenyanStudent) {
      const finalPhone = mpesaNumber.current?.value || '';
      const formattedPhone = formatPhoneNumber(finalPhone);

      if (!formattedPhone) {
        setIsLoading(false);
        setPaymentStatusText('Please enter a valid M-Pesa phone number.');
        return;
      }

      (async () => {
        try {
          const session = await callStudentPaymentApi('/student-payments/tuition/initiate', {
            method: 'POST',
            body: JSON.stringify({
              amount: finalAmountKes,
              phoneNumber: formattedPhone,
              selectedMonth,
              courseName: theCourseName,
              courseType: isSecondaryPayment ? 'secondary' : 'primary',
              intakeId: secondaryCourseContext?.intakeId || '',
              intakeDate: secondaryCourseContext?.intakeDate || '',
              rawIntakeDate: secondaryCourseContext?.rawIntakeDate || '',
              campus: secondaryCourseContext?.campus || '',
              description: paymentDesc,
            })
          });

          pollStudentPaymentSession({
            sessionId: session.sessionId,
            clearPollRef: clearPaymentPoll,
            setStatus: setPaymentStatusText,
            setLoading: setIsLoading,
            onSuccess: (result) => {
              toast.success('Payment received successfully.');
              if (result.autoAdmitted) {
                swal('Payment Successful', 'Your payment was received and your student account has been admitted.', 'success');
              } else if (isSecondaryPayment) {
                swal('Payment Successful', 'Your payment was received and the secondary course has been activated.', 'success');
              } else {
                swal('Payment Successful', 'Your payment was received successfully.', 'success');
              }
              window.location.reload();
            },
            onFailed: (result) => {
              swal('Payment not completed', result?.resultDesc || 'We could not confirm the payment. Please try again.', 'warning');
            }
          });
        } catch (error) {
          console.error('Error making payment:', error.message);
          setIsLoading(false);
          setPaymentStatusText(error.message || 'We could not send the prompt. Please try again.');
        }
      })();

      return;
    }

    if (!window.PaystackPop) {
      setIsLoading(false);
      setPaymentStatusText('Paystack is not available. Please refresh and try again.');
      swal('Payment Error', 'Paystack could not load. Please refresh this page and try again.', 'error');
      return;
    }

    const paystackAmount = getPaystackMinorAmountFromKes(finalAmountKes, usdToKesRate);
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: userEmail,
      amount: paystackAmount,
      currency: 'USD',
      ref: `UBN-${Date.now()}`,
      metadata: {
        custom_fields: [
          {
            display_name: 'Student Name',
            variable_name: 'student_name',
            value: bio,
          },
          {
            display_name: 'Course',
            variable_name: 'course',
            value: theCourseName,
          },
        ],
      },
      callback: function (response) {
        if (response?.status === 'success' || response?.reference) {
          setPaymentStatusText('Payment received. Finalizing...');
          processPayment('Paystack', 'USD', response.reference, response.trans || response.transaction || 'success', selectedMonth, finalAmountKes, displayAmount);
          return;
        }

        swal('Payment Error', 'Payment failed or was not successful', 'error');
        setIsLoading(false);
      },
      onClose: function () {
        swal('Payment Error', 'Transaction was not completed, action canceled', 'error');
        setIsLoading(false);
      }
    });

    handler.openIframe();
  };

  /////

  const generateManualProspectRef = (paymentId) => {
    const year = new Date().getFullYear();
    return `UBN-${year}-${paymentId.slice(0, 8).toUpperCase()}`;
  };

  const getRequestToken = async () => {
    if (token) return token;
    if (!auth.currentUser) return '';
    return auth.currentUser.getIdToken();
  };

  const callStudentPaymentApi = async (path, options = {}) => {
    const authToken = await getRequestToken();
    if (!authToken) {
      throw new Error('Your session has expired. Please log in again.');
    }

    const response = await fetch(`${STUDENT_PAYMENT_API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...(options.headers || {})
      }
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || 'Payment request failed.');
    }

    return payload;
  };

  const clearPaymentPoll = () => {
    if (paymentPollRef.current) {
      clearInterval(paymentPollRef.current);
      paymentPollRef.current = null;
    }
  };

  const clearAdmissionPaymentPoll = () => {
    if (admissionPaymentPollRef.current) {
      clearInterval(admissionPaymentPollRef.current);
      admissionPaymentPollRef.current = null;
    }
  };

  const pollStudentPaymentSession = ({ sessionId, onPending, onFailed, onSuccess, clearPollRef, setStatus, setLoading }) => {
    clearPollRef();
    let attempts = 0;
    const maxAttempts = 20;

    const poll = async () => {
      attempts += 1;
      try {
        const result = await callStudentPaymentApi(`/student-payments/${sessionId}/status`, {
          method: 'GET'
        });

        if (result.processingStatus === 'recorded' || result.paymentStatus === 'success') {
          clearPollRef();
          setLoading(false);
          setStatus('Payment received. Finalizing...');
          onSuccess(result);
          return;
        }

        if (result.paymentStatus === 'failed' || result.processingStatus === 'failed') {
          clearPollRef();
          setLoading(false);
          setStatus(result.resultDesc || 'Payment failed or cancelled.');
          onFailed(result);
          return;
        }

        setStatus(`Checking for payment... (${attempts}/${maxAttempts})`);
        onPending?.(result, attempts, maxAttempts);
        if (attempts >= maxAttempts) {
          clearPollRef();
          setLoading(false);
          setStatus("We haven't received payment yet. You can try again if needed.");
          swal('Still pending', "We haven't received the payment yet. If you already paid, it may take a moment.", 'info');
        }
      } catch (error) {
        if (attempts >= maxAttempts) {
          clearPollRef();
          setLoading(false);
          setStatus(error.message || 'We could not confirm the payment yet.');
          swal('Still pending', error.message || "We couldn't confirm the payment yet. Please wait a bit and try again.", 'info');
          return;
        }

        setStatus(`Checking for payment... (${attempts}/${maxAttempts})`);
      }
    };

    setStatus('Prompt sent. Waiting for payment confirmation...');
    const intervalId = setInterval(poll, 8000);
    if (clearPollRef === clearAdmissionPaymentPoll) {
      admissionPaymentPollRef.current = intervalId;
    } else {
      paymentPollRef.current = intervalId;
    }
  };

  const sendAdmissionFeeReceiptEmail = async ({ paymentReference, amountKes, displayAmount, displayCurrency, prospectAdmissionNumber }) => {
    if (!userEmail) return;

    const authToken = await getRequestToken();
    const sourceCurrency = resolvePaystackSourceCurrency(displayCurrency);
    const sourceAmountUsd = sourceCurrency === 'USD'
      ? Number(displayAmount || 0)
      : Number(amountKes || 0) / Number(usdToKesRate || 1);
    const emailMessage = buildAdmissionFeeReceiptEmail({
      studentName: bio,
      course: theCourseName,
      amount: amountKes,
      currency: 'KES',
      paymentReference,
      prospectAdmissionNumber: prospectAdmissionNumber || studentRecord.prospectAdmissionNumber || 'Will be assigned shortly',
      usdEquivalent: sourceAmountUsd,
      sourceCurrency,
      usdToKesRate,
    });

    await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        email: userEmail,
        subject: 'Admission fee received - Ubunifu College',
        message: emailMessage,
      })
    });
  };

  const sendAdmissionFeeSms = async ({ paymentReference, amountKes }) => {
    if (!isKenyanStudent || !thePhoneNumber) return;

    const authToken = await getRequestToken();
    const message = `Hi ${bio || 'there'}, we have received your Ubunifu College admission fee of KES ${Number(amountKes || 0).toLocaleString('en-KE')}. Ref: ${paymentReference}. Admissions will guide you on interview scheduling. - Ubunifu`;

    await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        phoneNumber: thePhoneNumber,
        message,
      })
    });
  };

  const recordAdmissionFeePayment = async ({ paymentMethod, paymentReference, responseTrans, displayAmount, displayCurrency, amountKes }) => {
    if (!userId) {
      throw new Error('Student account is not ready yet. Please refresh and try again.');
    }

    const paymentDate = Timestamp.now();
    const paymentDocId = buildPaystackPaymentDocId(paymentReference);
    const paymentRef = doc(db, 'AdmissionFeePayments', paymentDocId);
    const incomeRef = doc(db, 'Income', paymentDocId);
    const prospectAdmissionNumber = studentRecord.prospectAdmissionNumber || generateManualProspectRef(paymentDocId);
    const expectedAdmissionFeeAmount = admissionFeeAmountKes;
    const cumulativeAdmissionFeePaid = Math.max(0, admissionFeeAmountPaidKes + amountKes);
    const admissionFeeOutstanding = Math.max(0, expectedAdmissionFeeAmount - cumulativeAdmissionFeePaid);
    const admissionFeeFullyPaid = cumulativeAdmissionFeePaid >= expectedAdmissionFeeAmount;
    const admissionFeeStatus = admissionFeeFullyPaid ? 'paid' : 'partial';
    const sourceCurrency = resolvePaystackSourceCurrency(displayCurrency);
    const sourceAmountUsd = sourceCurrency === 'USD'
      ? Number(displayAmount || 0)
      : Number(amountKes || 0) / Number(usdToKesRate || 1);
    const conversionNote = sourceCurrency === 'USD'
      ? `Converted from USD at 1 USD = ${Number(usdToKesRate || 130).toLocaleString('en-KE')} KES`
      : '';
    const receiptNumber = Date.now();
    const batch = writeBatch(db);

    batch.set(incomeRef, {
      studentId: userId,
      incomeAmount: amountKes,
      amountInKes: amountKes,
      paymentDate,
      paymentMethod,
      createdBy: auth.currentUser?.uid || userId,
      timeCreated: paymentDate,
      paymentId: paymentDocId,
      invoiceNumber: receiptNumber,
      currency: 'KES',
      paymentCurrency: 'KES',
      displayCurrency: 'KES',
      displayAmount: amountKes,
      exchangeRateUsdToKes: sourceCurrency === 'USD' ? usdToKesRate : 1,
      courseName: theCourseName || studentRecord.course || '',
      courseType: isSecondaryPayment ? 'secondary' : 'primary',
      intakeId: secondaryCourseContext?.intakeId || '',
      intakeDate: secondaryCourseContext?.intakeDate || '',
      rawIntakeDate: secondaryCourseContext?.rawIntakeDate || '',
      responseReference: paymentReference,
      responseTrans: responseTrans || '',
      selectedMonth: '',
      paymentCategory: 'admission_fee',
      source: 'student_portal_paystack_web',
      sourceCurrency,
      sourceAmountUsd,
      sourceAmountKes: amountKes,
      conversionNote,
    }, { merge: true });

    batch.set(paymentRef, {
      userId,
      studentName: bio || '',
      studentEmail: userEmail || '',
      phoneNumber: thePhoneNumber || '',
      course: theCourseName || studentRecord.course || '',
      cohort: thecohort || studentRecord.cohort || '',
      prospectAdmissionNumber,
      amount: amountKes,
      currency: 'KES',
      expectedAdmissionFeeAmount,
      cumulativeAdmissionFeePaid,
      admissionFeeOutstanding,
      paymentStatus: admissionFeeStatus,
      admissionFeeSettingPath: `Settings/${ADMISSION_FEE_SETTINGS_DOC}`,
      paymentMethod,
      paybill: paymentMethod === 'Mpesa Web Payments' ? ADMISSION_MPESA_PAYBILL : '',
      accountNumber: paymentMethod === 'Mpesa Web Payments' ? prospectAdmissionNumber : '',
      paymentReference,
      responseReference: paymentReference,
      responseTrans: responseTrans || '',
      paymentDate,
      notes: 'Paid from student portal',
      source: 'student_portal_payment',
      recordedBy: userId,
      recordedByEmail: userEmail || '',
      amountInKes: amountKes,
      displayAmount: amountKes,
      displayCurrency: 'KES',
      paymentCurrency: 'KES',
      exchangeRateUsdToKes: sourceCurrency === 'USD' ? usdToKesRate : 1,
      sourceCurrency,
      sourceAmountUsd,
      sourceAmountKes: amountKes,
      conversionNote,
      createdAt: Timestamp.now(),
    }, { merge: true });

    if (!studentRecord.prospectAdmissionNumber) {
      batch.set(doc(db, 'AdmissionReferences', prospectAdmissionNumber), {
        prospectAdmissionNumber,
        userRef: `users/${userId}`,
        source: 'student_portal_admission_fee_payment',
        createdAt: Timestamp.now(),
      }, { merge: true });
    }

    const userUpdates = {
      prospectAdmissionNumber,
      admissionFeeStatus,
      admissionFeePaidAt: admissionFeeFullyPaid ? paymentDate : null,
      admissionFeeAmount: expectedAdmissionFeeAmount,
      admissionFeeAmountPaid: cumulativeAdmissionFeePaid,
      admissionFeeBalance: admissionFeeOutstanding,
      admissionFeePaymentMethod: paymentMethod,
      admissionFeePaymentReference: paymentReference,
      admissionFeePaymentId: paymentDocId,
      admissionFeeMessageStatus: studentRecord.admissionFeeMessageStatus === 'sent'
        ? 'sent'
        : (admissionFeeFullyPaid ? 'not_needed_paid' : 'pending_top_up'),
      admissionFeeRecordedManually: false,
      admissionFeeRecordedAt: Timestamp.now(),
      admissionFeeRecordedBy: userId,
      admissionFeeRecordedByEmail: userEmail || '',
      applicationStage: admissionFeeFullyPaid ? 'Admission Fee Paid' : 'Admission Fee Partially Paid',
      nextFollowUpStatus: admissionFeeFullyPaid ? 'cancelled' : null,
      nextFollowUpType: admissionFeeFullyPaid ? 'interview_scheduling' : null,
      nextFollowUpAt: null,
      updatedAt: Timestamp.now(),
    };

    batch.set(doc(db, 'users', userId), userUpdates, { merge: true });
    await batch.commit();

    setStudentRecord((previousRecord) => ({
      ...previousRecord,
      ...userUpdates,
    }));

    try {
      await sendAdmissionFeeReceiptEmail({
        paymentReference,
        amountKes,
        displayAmount,
        displayCurrency,
        prospectAdmissionNumber,
      });
    } catch (error) {
      console.error('Admission fee receipt email failed:', error);
    }

    try {
      await sendAdmissionFeeSms({ paymentReference, amountKes });
    } catch (error) {
      console.error('Admission fee SMS failed:', error);
    }
  };

  const completeAdmissionFeePayment = async ({ paymentMethod, paymentReference, responseTrans, displayAmount, displayCurrency, amountKes }) => {
    setAdmissionFeeStatusText('Payment received. Saving your admission step...');

    await recordAdmissionFeePayment({
      paymentMethod,
      paymentReference,
      responseTrans,
      displayAmount,
      displayCurrency,
      amountKes,
    });

    setIsAdmissionFeeLoading(false);
    setAdmissionFeeStatusText('');
    setShowAdmissionFeeModal(false);
    swal('Admission Fee Paid', 'Your admission fee has been received. Our admissions team will guide you on interview scheduling.', 'success');
  };

  const handleProceedToAdmissionFeePayment = () => {
    if (isAdmissionFeePaid) {
      swal('Admission Fee Paid', 'Your admission fee has already been received.', 'success');
      return;
    }

    if (!hasProspectAdmissionNumber) {
      setAdmissionFeeStatusText('Your admission reference is being prepared. Please check again shortly.');
      swal('Admission Reference Pending', 'Your admission reference is being prepared. Please check again shortly.', 'info');
      return;
    }

    if (admissionFeePromptAmountKes <= 0) {
      setAdmissionFeeStatusText('Your admission fee balance is already cleared. Please refresh your account.');
      swal('Admission Fee Updated', 'Your admission fee balance is already cleared. Please refresh your account.', 'info');
      return;
    }

    const displayCurrency = currentCurrencyCode;
    const displayAmount = getDisplayAmountFromKes(admissionFeePromptAmountKes, displayCurrency, usdToKesRate);
    setIsAdmissionFeeLoading(true);
    setAdmissionFeeStatusText(isKenyanStudent ? 'Sending admission fee prompt to your phone...' : 'Opening secure Paystack checkout...');

    if (isKenyanStudent) {
      const finalPhone = admissionMpesaNumber.current?.value || thePhoneNumber || '';
      const formattedPhone = formatPhoneNumber(finalPhone);

      if (!formattedPhone) {
        setIsAdmissionFeeLoading(false);
        setAdmissionFeeStatusText('Please enter a valid M-Pesa phone number.');
        return;
      }

      (async () => {
        try {
          const session = await callStudentPaymentApi('/student-payments/admission/initiate', {
            method: 'POST',
            body: JSON.stringify({
              phoneNumber: formattedPhone
            })
          });

          pollStudentPaymentSession({
            sessionId: session.sessionId,
            clearPollRef: clearAdmissionPaymentPoll,
            setStatus: setAdmissionFeeStatusText,
            setLoading: setIsAdmissionFeeLoading,
            onSuccess: () => {
              setShowAdmissionFeeModal(false);
              swal('Admission Fee Paid', 'Your admission fee has been received. Our admissions team will guide you on interview scheduling.', 'success');
              window.location.reload();
            },
            onFailed: (result) => {
              swal('Payment not completed', result?.resultDesc || 'We could not confirm the admission fee payment. Please try again.', 'warning');
            }
          });
        } catch (error) {
          console.error('Admission fee payment error:', error);
          setIsAdmissionFeeLoading(false);
          setAdmissionFeeStatusText(error.message || 'We could not send the prompt. Please try again.');
          swal('Payment Error', error.message || 'We could not send the payment prompt.', 'error');
        }
      })();

      return;
    }

    if (!window.PaystackPop) {
      setIsAdmissionFeeLoading(false);
      setAdmissionFeeStatusText('Paystack is not available. Please refresh and try again.');
      swal('Payment Error', 'Paystack could not load. Please refresh this page and try again.', 'error');
      return;
    }

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: userEmail,
      amount: getPaystackMinorAmountFromKes(admissionFeePromptAmountKes, usdToKesRate),
      currency: 'USD',
      ref: `UBN-ADM-${Date.now()}`,
      metadata: {
        custom_fields: [
          {
            display_name: 'Student Name',
            variable_name: 'student_name',
            value: bio,
          },
          {
            display_name: 'Payment Type',
            variable_name: 'payment_type',
            value: 'Admission Fee',
          },
        ],
      },
      callback: async function (response) {
        if (response?.status === 'success' || response?.reference) {
          try {
            await completeAdmissionFeePayment({
            paymentMethod: 'Paystack',
            paymentReference: response.reference,
            responseTrans: response.trans || response.transaction || 'success',
            displayAmount,
            displayCurrency,
            amountKes: admissionFeePromptAmountKes,
            });
          } catch (error) {
            console.error('Admission fee record error:', error);
            setIsAdmissionFeeLoading(false);
            setAdmissionFeeStatusText('Payment was received, but saving the record failed. Please contact admissions.');
            swal('Recording Error', error.message || 'Payment was received, but saving the admission fee record failed.', 'error');
          }
          return;
        }

        setIsAdmissionFeeLoading(false);
        swal('Payment Error', 'Payment failed or was not successful', 'error');
      },
      onClose: function () {
        setIsAdmissionFeeLoading(false);
        setAdmissionFeeStatusText('');
        swal('Payment Error', 'Transaction was not completed, action canceled', 'error');
      }
    });

    handler.openIframe();
  };

  const generateAdmissionNumber = () => {
    return Math.floor(100000 + Math.random() * 900000);
  };

  const reserveUniqueAdmissionNumber = async (studentDocId, userUpdates = {}) => {
    const maxAttempts = 25;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const candidateNumber = generateAdmissionNumber();
      const candidateKey = String(candidateNumber);
      const admissionNumberRef = doc(db, 'AdmissionNumbers', candidateKey);
      const userRef = doc(db, 'users', studentDocId);

      try {
        await runTransaction(db, async (transaction) => {
          const admissionNumberSnap = await transaction.get(admissionNumberRef);
          if (admissionNumberSnap.exists()) {
            throw new Error('ADMISSION_NUMBER_TAKEN');
          }

          transaction.set(admissionNumberRef, {
            admissionNumber: candidateNumber,
            studentId: userId || studentDocId,
            studentDocId,
            createdAt: Timestamp.now(),
            source: 'student_self_admission'
          });
          transaction.update(userRef, {
            ...userUpdates,
            admissionNumber: candidateNumber
          });
        });

        return candidateNumber;
      } catch (error) {
        if (error?.message === 'ADMISSION_NUMBER_TAKEN') {
          continue;
        }

        throw error;
      }
    }

    throw new Error('Unable to generate a unique admission number. Please try again.');
  };

  const processPayment = async (paymentMethod, currency, responseReference, responseTrans, selectedMonth, amount, displayAmount,) => {
    var timeStamp = new Date();
    toast.info(`setting up your students account....`);

    // Define options for formatting the date
    var options = {
      weekday: 'long', // Full name of the day
      day: 'numeric',  // Numeric day of the month
      month: 'long',   // Full name of the month
      year: 'numeric'  // Full year
    };

    // Format the timestamp using `toLocaleDateString`
    var paymentDate = timeStamp.toLocaleDateString('en-GB', options);
    const receiptNumber = Date.now();
    const invnumb = receiptNumber;

    let incomeAmount = amount;
    let studentsUserName = bio;
    let phoneNumber = thePhoneNumber;
    const sourceCurrency = currency === 'USD' ? 'USD' : 'KES';
    const receiptAmountKes = Number(amount || 0);
    const sourceAmountUsd = sourceCurrency === 'USD'
      ? Number(displayAmount || 0)
      : receiptAmountKes / Number(usdToKesRate || 1);
    const receiptAmountText = formatMoney(receiptAmountKes, 'KES');
    const conversionNote = sourceCurrency === 'USD'
      ? `Converted from USD at 1 USD = ${Number(usdToKesRate || 130).toLocaleString('en-KE')} KES`
      : '';
    const receiptEmail = buildReceiptEmail({
      studentName: studentsUserName,
      amount: receiptAmountKes,
      currency: 'KES',
      paymentDate: timeStamp,
      paymentMethod,
      receiptNumber: invnumb,
      usdEquivalent: sourceAmountUsd,
      sourceCurrency,
      usdToKesRate,
    });


    var sendEmail = '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">' +
      '<html xmlns:v="urn:schemas-microsoft-com:vml">' +
      '<head>' +
      '<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />' +
      '<meta name="viewport" content="width=device-width; initial-scale=1.0; maximum-scale=1.0;" />' +
      '<link href="https://fonts.googleapis.com/css?family=Work+Sans:300,400,500,600,700" rel="stylesheet">' +
      '<link href="https://fonts.googleapis.com/css?family=Quicksand:300,400,700" rel="stylesheet">' +
      '<title>Payment Receipt</title>' +
      '<style type="text/css">' +
      'body {' +
      'width: 100%;' +
      'background-color: #ffffff;' +
      'margin: 0;' +
      'padding: 0;' +
      '-webkit-font-smoothing: antialiased;' +
      'mso-margin-top-alt: 0px;' +
      'mso-margin-bottom-alt: 0px;' +
      'mso-padding-alt: 0px 0px 0px 0px;' +
      '}' +
      'p, h1, h2, h3, h4 {' +
      'margin-top: 0;' +
      'margin-bottom: 0;' +
      'padding-top: 0;' +
      'padding-bottom: 0;' +
      '}' +
      'span.preheader {' +
      'display: none;' +
      'font-size: 1px;' +
      '}' +
      'html {' +
      'width: 100%;' +
      '}' +
      'table {' +
      'font-size: 14px;' +
      'border: 0;' +
      '}' +
      '@media only screen and (max-width: 640px) {' +
      '.main-header {' +
      'font-size: 20px !important;' +
      '}' +
      '.main-section-header {' +
      'font-size: 28px !important;' +
      '}' +
      '.show {' +
      'display: block !important;' +
      '}' +
      '.hide {' +
      'display: none !important;' +
      '}' +
      '.align-center {' +
      'text-align: center !important;' +
      '}' +
      '.no-bg {' +
      'background: none !important;' +
      '}' +
      '.main-image img {' +
      'width: 440px !important;' +
      'height: auto !important;' +
      '}' +
      '.divider img {' +
      'width: 440px !important;' +
      '}' +
      '.container590 {' +
      'width: 440px !important;' +
      '}' +
      '.container580 {' +
      'width: 400px !important;' +
      '}' +
      '.main-button {' +
      'width: 220px !important;' +
      '}' +
      '.section-img img {' +
      'width: 320px !important;' +
      'height: auto !important;' +
      '}' +
      '.team-img img {' +
      'width: 100% !important;' +
      'height: auto !important;' +
      '}' +
      '}' +
      '@media only screen and (max-width: 479px) {' +
      '.main-header {' +
      'font-size: 18px !important;' +
      '}' +
      '.main-section-header {' +
      'font-size: 26px !important;' +
      '}' +
      '.divider img {' +
      'width: 280px !important;' +
      '}' +
      '.container590 {' +
      'width: 280px !important;' +
      '}' +
      '.container580 {' +
      'width: 260px !important;' +
      '}' +
      '.section-img img {' +
      'width: 280px !important;' +
      'height: auto !important;' +
      '}' +
      '}' +
      '</style>' +
      '</head>' +
      '<body class="respond" leftmargin="0" topmargin="0" marginwidth="0" marginheight="0">' +
      '<table border="0" width="100%" cellpadding="0" cellspacing="0" bgcolor="ffffff">' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" align="center" width="590" cellpadding="0" cellspacing="0" class="container590">' +
      '<tr>' +
      '<td height="25" style="font-size: 25px; line-height: 25px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" align="center" width="590" cellpadding="0" cellspacing="0" class="container590">' +
      '<tr>' +
      '<td align="center" height="70" style="height:70px;">' +
      '<a href="" style="display: block; border-style: none !important; border: 0 !important;"><img width="100" border="0" style="display: block; width: 150px;" src="https://ubunifucollege.com/assets/images/logo-main-black.png" alt="" /></a>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="25" style="font-size: 25px; line-height: 25px;">&nbsp;</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '<table border="0" width="100%" cellpadding="0" cellspacing="0" bgcolor="ffffff" class="bg_color">' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" align="center" width="590" cellpadding="0" cellspacing="0" class="container590">' +
      '<tr>' +
      '<td height="20" style="font-size: 20px; line-height: 20px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center" style="color: #343434; font-size: 24px; font-family: Quicksand, Calibri, sans-serif; font-weight:700; letter-spacing: 3px; line-height: 35px;" class="main-header">' +
      '<div style="line-height: 35px">PAYMENT RECEIPT</div>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="10" style="font-size: 10px; line-height: 10px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" width="40" align="center" cellpadding="0" cellspacing="0" bgcolor="eeeeee">' +
      '<tr>' +
      '<td height="2" style="font-size: 2px; line-height: 2px;">&nbsp;</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="10" style="font-size: 10px; line-height: 10px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center" style="color: #888888; font-size: 18px; font-family: Work Sans, Calibri, sans-serif; line-height: 23px;" class="text_color">' +
      '<div style="color: #333333; font-size: 18px; font-family: Work Sans, Calibri, sans-serif; font-weight: 600; mso-line-height-rule: exactly; line-height: 23px;">' +
      'Receipt #' + invnumb + '' +
      '</div>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="10" style="font-size: 10px; line-height: 10px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" width="400" align="center" cellpadding="0" cellspacing="0" class="container590">' +
      '<tr>' +
      '<td align="center" style="color: #888888; font-size: 16px; font-family: Work Sans, Calibri, sans-serif; line-height: 24px;">' +
      '<div style="line-height: 24px">' +
      'for ' + studentsUserName + ' <br/>' +
      'Paid on ' + paymentDate + '' +
      '</div>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="25" style="font-size: 25px; line-height: 25px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center" style="border-top: 1px solid #e0e0e0; border-bottom: 1px solid #e0e0e0; font-size: 10px;">' +
      '<table border="0" width="400" align="center" cellpadding="0" cellspacing="0" class="container590">' +
      '<tr>' +
      '<td align="center" style="color: #000; font-size: 18px; font-family: Work Sans, Calibri, sans-serif; line-height: 24px; padding-top:20px; padding-bottom:20px;">' +
      '<div style="line-height: 24px">' +
      'Payment Amount: ' + receiptAmountText + '' +
      '</div>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="35" style="font-size: 25px; line-height: 25px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" width="400" align="center" cellpadding="0" cellspacing="0" class="container590">' +
      '<tr>' +
      '<td align="center" style="color: #888888; font-size: 16px; font-family: Work Sans, Calibri, sans-serif; line-height: 24px;">' +
      '<div style="line-height: 24px">' +
      'Payment Method: ' + paymentMethod + '' +
      '</div>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="20" style="font-size: 25px; line-height: 25px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center">' +
      '<table border="0" align="center" width="160" cellpadding="0" cellspacing="0" bgcolor="5caad2">' +
      '<tr>' +
      '<td height="10" style="font-size: 10px; line-height: 10px;">&nbsp;</td>' +
      '</tr>' +
      '<tr>' +
      '<td align="center" style="color: #ffffff; font-size: 14px; font-family: Work Sans, Calibri, sans-serif; line-height: 26px;">' +
      '<div style="line-height: 26px;">' +
      '<a href="https://live.ubunifucollege.com/view-receipt?' + invnumb + '" style="color: #ffffff; text-decoration: none;">VIEW RECEIPT</a>' +
      '</div>' +
      '</td>' +
      '</tr>' +
      '<tr>' +
      '<td height="10" style="font-size: 10px; line-height: 10px;">&nbsp;</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '</td>' +
      '</tr>' +
      '</table>' +
      '</body>' +
      '</html>';



    try {
      const paymentTimestamp = Timestamp.now();
      const paymentDocId = buildPaystackPaymentDocId(responseReference);
      const settledAmountKes = Number(incomeAmount || 0);
      const sourceAmountUsd = sourceCurrency === 'USD'
        ? Number(displayAmount || 0)
        : settledAmountKes / Number(usdToKesRate || 1);
      const paymentRecord = {
        studentId: userId,
        incomeAmount: settledAmountKes,
        amountInKes: settledAmountKes,
        paymentDate: paymentTimestamp,
        paymentMethod: paymentMethod,
        createdBy: auth.currentUser?.uid || userId,
        timeCreated: paymentTimestamp,
        paymentId: paymentDocId,
        invoiceNumber: invnumb,
        currency: 'KES',
        paymentCurrency: 'KES',
        displayCurrency: 'KES',
        responseReference,
        responseTrans,
        selectedMonth,
        displayAmount: settledAmountKes,
        exchangeRateUsdToKes: sourceCurrency === 'USD' ? usdToKesRate : 1,
        sourceCurrency,
        sourceAmountUsd,
        sourceAmountKes: settledAmountKes,
        conversionNote,
        courseName: theCourseName,
        courseType: isSecondaryPayment ? "secondary" : "primary",
        intakeId: secondaryCourseContext?.intakeId || "",
        intakeDate: secondaryCourseContext?.intakeDate || "",
        rawIntakeDate: secondaryCourseContext?.rawIntakeDate || "",
        paymentCategory: 'tuition_fee',
        source: 'student_portal_paystack_web',
      };

      await setDoc(doc(db, "Income", paymentDocId), paymentRecord, { merge: true });

      const emailSubject = "Ubunifu College Payment Receipt #" + invnumb + "";
      const emailAddress = userEmail;

      const emailData = {
        email: emailAddress,
        message: receiptEmail || sendEmail,
        subject: emailSubject
      };

      await fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(emailData)
      });

      //if the student is already admitted dont send an admission letter
      toast.info(`setting up your account....`);

      if (sourceCurrency === 'KES') {
        await sendSMS(studentsUserName, settledAmountKes, paymentDate, invnumb, phoneNumber, 'KES', settledAmountKes, token);
        return;
      }

      await finalizeStudentPayment();

    } catch (error) {
      console.error("Error adding document: ", error);
      swal('Error!', 'An error occured!', 'error');
    }
  };

  const finalizeStudentPayment = async () => {
    if (isSecondaryPayment) {
      await activateSecondaryCourse();
      swal("Success!", "Payment successfully received. Secondary course admitted.", "success");
      toast.success(`Secondary course admitted`);
      window.location.reload();
      return;
    }

    if (studentStatus === "applicant") {
      await handleAdmission();
      toast.info(`Please wait. Setting up your account....`);
      return;
    }

    swal("Success!", "Payment successfully received", "success");
    toast.success(`Payment successfully received`);
    window.location.reload();
  };

  const sendSMS = async (studentName, amount, paymentDate, receiptNumber, phoneNumber, currency, displayAmount) => {
    const message = `Dear ${studentName}, we have received your payment of ${formatMoney(displayAmount, currency)} on ${paymentDate}. View your receipt here https://live.ubunifucollege.com/view-receipt?${receiptNumber}. Thank you for your timely payment. - Ubunifu College`;
    const smsData = {
      phoneNumber: phoneNumber,
      message: message
    };

    try {
      const response = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(smsData)
      });

      const data = await response.json();
      console.log(data);

      await finalizeStudentPayment();
      return true;
    } catch (error) {
      console.error("Error sending SMS: ", error);
      await finalizeStudentPayment();
      return false;
    }
  };


  //end payment logic

  const activateSecondaryCourse = async () => {
    if (!isSecondaryPayment || !userDocId || !secondaryCourseContext) return;
    const targetName = normalizeCourseName(secondaryCourseContext.name);
    const targetIntakeId = secondaryCourseContext.intakeId || "";
    const updatedSecondary = (Array.isArray(secondaryCourses) ? secondaryCourses : []).map((entry) => {
      if (typeof entry === "string") {
        if (normalizeCourseName(entry) !== targetName) return entry;
        return {
          name: entry,
          status: "Active",
          admissionDate: Timestamp.now(),
          intakeId: targetIntakeId,
          intakeDate: secondaryCourseContext.intakeDate || "",
          rawIntakeDate: secondaryCourseContext.rawIntakeDate || "",
          campus: secondaryCourseContext.campus || "",
        };
      }
      const entryName = normalizeCourseName(entry?.name || entry?.courseName);
      const entryIntakeId = entry?.intakeId || "";
      if (entryName !== targetName) return entry;
      if (targetIntakeId && entryIntakeId && entryIntakeId !== targetIntakeId) return entry;
      return {
        ...entry,
        status: "Active",
        admissionDate: Timestamp.now(),
        intakeId: entryIntakeId || targetIntakeId,
        intakeDate: entry?.intakeDate || secondaryCourseContext.intakeDate || "",
        rawIntakeDate: entry?.rawIntakeDate || secondaryCourseContext.rawIntakeDate || "",
        campus: entry?.campus || secondaryCourseContext.campus || "",
      };
    });

    try {
      await updateDoc(doc(db, 'users', userDocId), {
        secondaryCourses: updatedSecondary,
        pendingSecondaryCourse: null
      });
      setSecondaryCourses(updatedSecondary);
      setSecondaryCourseContext(null);
      setIsSecondaryPayment(false);
    } catch (error) {
      console.error("Error activating secondary course: ", error);
    }
  };

  const handleAdmission = async () => {
    const targetUserDocId = userDocId || userId;
    if (!targetUserDocId) {
      swal("Error", "We could not find your student record. Please refresh and try again.", "error");
      return;
    }

    const mcourseDuration = courseDetails.mcourseDuration;
    const userName = bio;
    const thecourse = theCourseName;

    try {
      const admissionNumber = await reserveUniqueAdmissionNumber(targetUserDocId, {
        userStatus: "Student",
        Status: "Active",
        admissionDate: Timestamp.now()
      });
      const uniformAdmissionEmailTemplate = buildAdmissionConfirmationEmail({
        studentName: userName,
        admissionNumber,
        course: thecourse,
        duration: mcourseDuration || programDuration,
        admissionDate: Timestamp.now(),
        classStartDate: thecohort,
        cohort: thecohort
      });

      const newMail = doc(collection(db, "mail"));
      setDoc(newMail, {
        to: userEmail,
        message: {
          subject: '🎉 ADMISSION CONFIRMATION',
          text: 'Hi  user, ',
          html: uniformAdmissionEmailTemplate,
        }
      }).then(() => {
        swal("Success!", "Payment successfully received. Check your email for whats next.", "success");
        toast.success(`Payment successfully received. Check your email for whats next`);
        window.location.reload();
        //write here an actoion after an email has been delivered 
      })


    } catch (error) {
      console.error('Error updating document:', error);
      swal("Error", "An error occurred while admitting the student", "error");
    }
  };



  // READ PERSONAL DETAILS

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsPageLoading(false);
        return;
      }
      setIsPageLoading(true);
      const uid = user.uid;
      setUserId(uid);

      const userQuery = query(collection(db, "users"), where("userId", "==", uid));
      const userSnapshot = await getDocs(userQuery);

      let courseName = "";
      let bio = "";
      let userEmail = "";
      let phoneNumber = "";
      let userStatus = "";
      let Status = "";
      let cohort = "";
      let admissionDate = "";
      let pendingSecondaryCourse = null;
      let secondaryList = [];
      let resolvedStudentCurrency = 'KES';

      userSnapshot.forEach((docSnap) => {
        const userData = docSnap.data();
        setUserDocId(docSnap.id);
        setStudentRecord({ id: docSnap.id, ...userData });
        courseName = userData.course;
        bio = userData.bio;
        userEmail = userData.userEmail;
        phoneNumber = userData.phoneNumber;
        userStatus = userData.userStatus;
        Status = userData.Status;
        cohort = userData.cohort;
        admissionDate = userData.admissionDate || "";
        pendingSecondaryCourse = userData.pendingSecondaryCourse || null;
        secondaryList = userData.secondaryCourses || [];
        resolvedStudentCurrency = resolveStudentCurrency(userData);
      });

      setBio(bio);
      setUserEmail(userEmail);
      setPhoneNumber(phoneNumber);
      setCohort(cohort);
      setStudentCurrencyCode(resolvedStudentCurrency);

      let studentAdmissionStatus = "applicant";
      if (canContinueTuitionWithoutAdmissionFee({ userStatus, Status })) {
        studentAdmissionStatus = "student";
      }
      setstudentStatus(studentAdmissionStatus);

      const normalizedSecondary = Array.isArray(secondaryList)
        ? secondaryList.map((entry) => {
          if (typeof entry === "string") {
            return { name: entry, status: "Pending" };
          }
          return {
            name: entry?.name || entry?.courseName || "",
            intakeId: entry?.intakeId || "",
            intakeDate: entry?.intakeDate || entry?.intake || "",
            rawIntakeDate: entry?.rawIntakeDate || "",
            campus: entry?.campus || "",
            status: entry?.status || "Pending",
            admissionDate: entry?.admissionDate || ""
          };
        }).filter((entry) => entry.name)
        : [];
      setSecondaryCourses(normalizedSecondary);

      const coursesSnapshot = await getDocs(collection(db, "courses"));
      const catalog = {};
      coursesSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const name = data.mcourseName || data.courseName || data.name;
        if (!name) return;
        const key = normalizeCourseName(name);
        catalog[key] = {
          name,
          feeDeposit: +data.feeDeposit || 0,
          feeInstallments: +data.feeInstallments || 0,
          mcoursePrice: +data.mcoursePrice || 0,
          mcourseDuration: +data.courseDuration || +data.mcourseDuration || 0,
          durationLabel: data.mcourseDuration || data.courseDuration || ""
        };
      });
      setCourseCatalog(catalog);

      const incomeQuery = query(collection(db, "Income"), where("studentId", "==", uid));
      const incomeSnapshot = await getDocs(incomeQuery);
      const payments = {};
      const currentMonth = new Date().getMonth() + 1;
      const primaryKey = normalizeCourseName(courseName);
      if (primaryKey) {
        payments[primaryKey] = { totalPaid: 0, paidThisMonth: 0 };
      }
      incomeSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const amount = parseInt(data.incomeAmount, 10) || 0;
        const paymentCourse = data.courseName || data.course || data.theCourseName || courseName;
        const courseKey = normalizeCourseName(paymentCourse || courseName);
        if (!payments[courseKey]) {
          payments[courseKey] = { totalPaid: 0, paidThisMonth: 0 };
        }
        payments[courseKey].totalPaid += amount;
        const paymentDate = data.paymentDate?.toDate ? data.paymentDate.toDate() : new Date(data.paymentDate?.seconds * 1000);
        if (paymentDate && paymentDate.getMonth() + 1 === currentMonth) {
          payments[courseKey].paidThisMonth += amount;
        }
      });
      setPaymentMap(payments);

      const tabs = [];
      if (courseName) {
        tabs.push({
          key: buildCourseKey(courseName),
          name: courseName,
          type: "primary",
          status: studentAdmissionStatus === "student" ? "Active" : "Pending",
          admissionDate
        });
      }
      normalizedSecondary.forEach((entry) => {
        const key = buildCourseKey(entry.name, entry.intakeId, entry.rawIntakeDate);
        if (tabs.find((tab) => tab.key === key)) return;
        tabs.push({
          key,
          name: entry.name,
          type: "secondary",
          intakeId: entry.intakeId,
          intakeDate: entry.intakeDate,
          rawIntakeDate: entry.rawIntakeDate,
          campus: entry.campus,
          status: entry.status,
          admissionDate: entry.admissionDate
        });
      });

      const preferredSecondary = secondaryFromRoute || pendingSecondaryCourse;
      if (preferredSecondary?.name) {
        const key = buildCourseKey(preferredSecondary.name, preferredSecondary.intakeId, preferredSecondary.rawIntakeDate);
        if (!tabs.find((tab) => tab.key === key)) {
          tabs.push({
            key,
            name: preferredSecondary.name,
            type: "secondary",
            intakeId: preferredSecondary.intakeId || "",
            intakeDate: preferredSecondary.intakeDate || "",
            rawIntakeDate: preferredSecondary.rawIntakeDate || "",
            campus: preferredSecondary.campus || "",
            status: preferredSecondary.status || "Pending",
            admissionDate: preferredSecondary.admissionDate || ""
          });
        }
      }

      setCourseTabs(tabs);

      const preferredKey = preferredSecondary?.name
        ? buildCourseKey(preferredSecondary.name, preferredSecondary.intakeId, preferredSecondary.rawIntakeDate)
        : buildCourseKey(courseName);
      setActiveCourseKey(preferredKey);
      setIsPageLoading(false);
    });

    return () => unsubscribe();
  }, [auth, secondaryFromRoute]);

  const activeCourse = courseTabs.find((tab) => tab.key === activeCourseKey) || courseTabs[0] || null;

  useEffect(() => {
    if (!routerLocation?.state?.focusAdmissionFee || !admissionFeeCardRef.current) return;
    admissionFeeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [routerLocation?.state?.focusAdmissionFee, shouldShowAdmissionFeeCard]);

  useEffect(() => {
    if (!activeCourse && courseTabs.length) {
      setActiveCourseKey(courseTabs[0].key);
    }
  }, [activeCourse, courseTabs]);

  useEffect(() => {
    if (!activeCourse) return;
    const courseKey = normalizeCourseName(activeCourse.name);
    const courseData = courseCatalog[courseKey] || {
      feeDeposit: 0,
      feeInstallments: 0,
      mcoursePrice: 0,
      mcourseDuration: 0,
      durationLabel: ""
    };
    const payments = paymentMap[courseKey] || { totalPaid: 0, paidThisMonth: 0 };

    setCourseDetails(courseData);
    setThecourseName(activeCourse.name);
    settheDuration(courseData.durationLabel || "");
    setAmountPaid(payments.totalPaid);
    setAmountPaidThisMonth(payments.paidThisMonth);
    setTotalRemainingBalance(courseData.mcoursePrice - payments.totalPaid);
    setInvoices(buildInvoicesForCourse(courseData, payments.totalPaid, {
      admissionDate: activeCourse.admissionDate,
      courseStatus: activeCourse.status
    }));

    if (activeCourse.type === "secondary") {
      setSecondaryCourseContext(activeCourse);
      setIsSecondaryPayment(true);
    } else {
      setSecondaryCourseContext(null);
      setIsSecondaryPayment(false);
    }
  }, [activeCourse, courseCatalog, paymentMap]);

  const dueMap = courseTabs.reduce((acc, tab) => {
    const courseKey = normalizeCourseName(tab.name);
    const courseData = courseCatalog[courseKey];
    if (!courseData) {
      acc[tab.key] = 0;
      return acc;
    }
    const payments = paymentMap[courseKey] || { totalPaid: 0 };
    const invoiceList = buildInvoicesForCourse(courseData, payments.totalPaid, {
      admissionDate: tab.admissionDate,
      courseStatus: tab.status
    });
    acc[tab.key] = invoiceList.filter((invoice) => invoice.status !== "Paid").length;
    return acc;
  }, {});

  const isDiscountEligible = courseDetails.mcoursePrice >= 70000;
  const discountRate = isDiscountEligible ? 0.1 : 0;
  const discountedFullPayment = Math.max(0, totalRemainingBalance * (1 - discountRate));
  const canPayInFull = totalRemainingBalance > 0;

  const handlePayInFull = () => {
    if (!canPayInFull) return;
    const label = isDiscountEligible ? "Full Payment (10% Discount)" : "Full Payment";
    setSelectedPaymentType("full");
    setSelectedInvoiceExpectedAmount(0);
    setSelectedInvoiceBalanceAmount(totalRemainingBalance);
    setAmountToPay(discountedFullPayment);
    setCustomPaymentAmount("");
    setSelectedMonth(label);
  };

  useEffect(() => {
    if (amountToPay === 0) {
      if (paymentPollRef.current) {
        clearInterval(paymentPollRef.current);
        paymentPollRef.current = null;
      }
      setIsLoading(false);
      setPaymentStatusText("");
    }
  }, [amountToPay]);


  //format the phone number 
  const formatPhoneNumber = (number) => {
    // Strip non-numeric characters
    let cleaned = number.replace(/[^0-9]/g, '');

    // Handle various cases
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.slice(1); // Replace leading 0 with 254
    } else if (cleaned.startsWith('254')) {
      // No change needed
    } else if (cleaned.startsWith('7')) {
      cleaned = '254' + cleaned; // Add 254 if starts with 7
    } else if (cleaned.startsWith('+254')) {
      cleaned = cleaned.slice(1); // Remove the + sign
    } else if (cleaned.startsWith('1')) {
      cleaned = '254' + cleaned; // For landlines or similar cases starting with 1
    }

    // Ensure the number has 12 digits
    if (cleaned.length === 12 && cleaned.startsWith('254')) {
      return cleaned;
    } else {
      setError('Please enter a valid phone number in the format 254XXXXXXXXX');
      return null;
    }
  };

  return (
    <div className="feeComp">
      <div className="feeTopDiv">
        <h6>Fee Payments</h6>

        <div className="feeItemsCont">
          <div className="feeItem">
            <div className='feeItemTop'>
              <FontAwesomeIcon icon={faCloud} className='' />
              {isPageLoading ? (
                <div className="skeleton-line feeSkeletonLine" />
              ) : (
                <p>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(courseDetails.mcoursePrice * conversionRate)} {currentCurrencyCode}
                </p>
              )}
            </div>
            <p className="feeItemP">Current course fee</p>
          </div>
          <div className="feeItem">
            <div className='feeItemTop'>
              <FontAwesomeIcon icon={faCloud} className='' />
              {isPageLoading ? (
                <div className="skeleton-line feeSkeletonLine" />
              ) : (
                <p>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(courseDetails.feeInstallments * conversionRate)} {currentCurrencyCode}
                </p>
              )}
            </div>
            <p className="feeItemP">Monthly Installments</p>
          </div>
          <div className="feeItem">
            <div className='feeItemTop'>
              <FontAwesomeIcon icon={faCloud} className='' />
              {isPageLoading ? (
                <div className="skeleton-line feeSkeletonLine" />
              ) : (
                <p>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(amountPaid * conversionRate)} {currentCurrencyCode}
                </p>
              )}
            </div>
            <p className="feeItemP">Amount Paid</p>
          </div>
          <div className="feeItem">
            <div className='feeItemTop'>
              <FontAwesomeIcon icon={faCloud} className='' />
              {isPageLoading ? (
                <div className="skeleton-line feeSkeletonLine" />
              ) : (
                <p>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(totalRemainingBalance * conversionRate)} {currentCurrencyCode}
                </p>
              )}
            </div>
            <p className="feeItemP">Total Fee Balance</p>
          </div>
        </div>
      </div>

      <div className="feeCourseTabs">
        {isPageLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div className="feeCourseTab feeCourseTab--skeleton" key={`tab-skeleton-${index}`}>
              <div className="skeleton-line skeleton-line--title" />
              <div className="skeleton-line" />
              <div className="skeleton-pill" />
            </div>
          ))
        ) : courseTabs.map((tab) => {
          const isActive = tab.key === activeCourseKey;
          const dueCount = dueMap[tab.key] || 0;
          const metaLabel =
            tab.type === "secondary"
              ? (tab.intakeDate ? `Intake ${tab.intakeDate}` : "Secondary course")
              : "Primary course";
          return (
            <button
              key={tab.key}
              className={`feeCourseTab ${isActive ? "active" : ""}`}
              onClick={() => setActiveCourseKey(tab.key)}
              type="button"
            >
              <span className="feeCourseTabTitle">{tab.name}</span>
              <span className="feeCourseTabMeta">{metaLabel}</span>
              <span className={`feeCourseTabBadge ${dueCount ? "due" : ""}`}>
                {dueCount ? `${dueCount} due` : "Paid"}
              </span>
            </button>
          );
        })}
      </div>

      {shouldShowAdmissionFeeCard && (
        <div ref={admissionFeeCardRef} className={`admissionFeeStudentCard ${isAdmissionFeePaid ? 'paid' : ''}`}>
          <div className="admissionFeeStudentMain">
            <span className="admissionFeeEyebrow">Admission step</span>
            <h6>{admissionCardTitle}</h6>
            <p>{admissionCardDescription}</p>
          </div>
          <div className="admissionFeeStudentMeta">
            <span>{admissionCardMetaLabel}</span>
            <strong>{admissionCardMetaValue}</strong>
            <small>{admissionCardMetaNote}</small>
          </div>
          {!isAdmissionFeePaid && hasProspectAdmissionNumber && (
            <button className="admissionFeeStudentButton" type="button" onClick={() => setShowAdmissionFeeModal(true)}>
              {hasPartialAdmissionFeePayment ? 'Top Up Admission Fee' : 'Pay Admission Fee'}
            </button>
          )}
        </div>
      )}

      <div className="feeFullPayCard feeInvoiceCard">
        <div>
          <h6>Pay in Full</h6>
          <p>
            Clear your balance in one payment.
            {isDiscountEligible && (
              <span className="feeDiscountNote"> 10% discount applied for courses 70,000+.</span>
            )}
          </p>
        </div>
        <div className="feeFullPayMeta">
          <div className="feeFullPayAmounts">
            {isPageLoading ? (
              <>
                <span className="feeFullPayOriginal">
                  <span className="skeleton-line feeSkeletonLine" />
                </span>
                <span className="feeFullPayAmount">
                  <span className="skeleton-line feeSkeletonLine" />
                </span>
              </>
            ) : (
              <>
                {isDiscountEligible && (
                  <span className="feeFullPayOriginal">
                    {(totalRemainingBalance * conversionRate).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} {currentCurrencyCode}
                  </span>
                )}
                <span className="feeFullPayAmount">
                  {(discountedFullPayment * conversionRate).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} {currentCurrencyCode}
                </span>
              </>
            )}
          </div>
          <button
            className="feeFullPayButton"
            type="button"
            onClick={handlePayInFull}
            disabled={!canPayInFull || isPageLoading || shouldRequireAdmissionFeeBeforeTuition}
          >
            {shouldRequireAdmissionFeeBeforeTuition ? 'Admission Fee First' : 'Pay in Full'}
          </button>
        </div>
      </div>

      <div className="feeInvoiceHeader feeInvoiceCard">
        <h6 className="feeTransactionHeading">Monthly Invoices {theCourseName ? `- ${theCourseName}` : ""}</h6>
        <p className="feeInvoiceSubtext">
          Prefer monthly payments? Use the schedule below to pay each month as it becomes due.
        </p>
      </div>
      <div className="feeTableCont feeInvoiceCard table-responsive">
        <Table striped className="feeTable feeTablePretty">
          <thead>
            <tr>
              <th>Month</th>
              <th>Expected Amount</th>
              <th>Paid Amount</th>
              <th>Status</th>
              <th>Make Payment</th>
            </tr>
          </thead>
          <tbody>
            {isPageLoading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <tr key={`invoice-skeleton-${index}`}>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-button" /></td>
                </tr>
              ))
            ) : invoices.map((invoice, index) => (
              <tr key={index}>
                <td data-label="Month">{invoice.month}</td>
                <td data-label="Expected Amount">
                  {(invoice.expectedAmount * conversionRate).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} {currentCurrencyCode}
                </td>
                <td data-label="Paid Amount">
                  {(invoice.paidAmount * conversionRate).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} {currentCurrencyCode}
                </td>

                <td data-label="Status">
                  <span className={`feeStatusBadge feeStatusBadge${invoice.status.replace(/\s+/g, "")}`}>
                    {invoice.status}
                  </span>
                </td>
                <td data-label="Payment">
                  {invoice.status !== "Paid" && (
                    <button
                      className="btn btn-success feeButton"
                      onClick={() => handleMakePayment(invoice.expectedAmount, invoice.paidAmount, invoice.month)}
                      disabled={shouldRequireAdmissionFeeBeforeTuition}
                    >
                      {shouldRequireAdmissionFeeBeforeTuition ? 'Admission Fee First' : 'Make Payment'}
                    </button>
                  )}
                </td>

                {/* <td>
                {invoice.status !== "Paid" && (
                  <a 
                    className="btn btn-primary"
                    href='https://app.withlotus.com/business/001102'
                  >
                    Make Payment
                  </a>
                )}
              </td> */}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>


      <Modal
        show={showAdmissionFeeModal}
        onHide={() => !isAdmissionFeeLoading && setShowAdmissionFeeModal(false)}
        dialogClassName="feePaymentModal"
        contentClassName="feePaymentModalContent"
        centered
      >
        <Modal.Header closeButton className="feePaymentModalHeader">
          <Modal.Title>{isKenyanStudent ? 'Admission Fee Balance - M-Pesa' : 'Admission Fee Balance - Card Payment'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="feePaymentModalBody">
          <div className="feePaymentAmount">
            <span>Total to Pay</span>
            <strong>{admissionFeeRemainingDisplayAmount}</strong>
            <small>{hasProspectAdmissionNumber ? `Application Ref: ${prospectAdmissionNumber}` : 'Admission fee'}</small>
          </div>

          {isKenyanStudent ? (
            <div className="feePaymentField">
              <label htmlFor="admissionMpesaNumber">M-Pesa Phone Number</label>
              <input
                id="admissionMpesaNumber"
                type="number"
                className="form-control"
                ref={admissionMpesaNumber}
                defaultValue={thePhoneNumber || ''}
                placeholder="Enter your M-Pesa number"
              />
              <p className="feePaymentHint">Paid so far: {admissionFeePaidDisplayAmount}. Remaining balance: {admissionFeeRemainingDisplayAmount}.</p>
              <p className="feePaymentHint">We’ll send a {formatMoneyFromKes(admissionFeePromptAmountKes, 'KES', usdToKesRate)} prompt to your phone.</p>
            </div>
          ) : (
            <div className="feePaymentField">
              <p className="feePaymentHint">Paid so far: {admissionFeePaidDisplayAmount}. Remaining balance: {admissionFeeRemainingDisplayAmount}.</p>
              <p className="feePaymentHint">International admission fee payments are processed in USD via Paystack. The school records the official fee as {formatMoneyFromKes(admissionFeeAmountKes, 'KES', usdToKesRate)}.</p>
            </div>
          )}

          {admissionFeeStatusText && (
            <p className="feePaymentStatus">{admissionFeeStatusText}</p>
          )}
        </Modal.Body>
        <Modal.Footer className="feePaymentModalFooter">
          <Button variant="secondary" onClick={() => setShowAdmissionFeeModal(false)} disabled={isAdmissionFeeLoading}>
            Cancel
          </Button>
          {isAdmissionFeeLoading ? (
            <Button variant="primary" disabled>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              Processing...
            </Button>
          ) : (
            <Button variant="primary" onClick={handleProceedToAdmissionFeePayment}>
              {isKenyanStudent ? 'Send M-Pesa Prompt' : 'Pay with Paystack'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal */}
      <Modal
        show={amountToPay > 0}
        onHide={() => {
          setAmountToPay(0);
          setSelectedMonth("");
          setCustomPaymentAmount("");
          setSelectedInvoiceExpectedAmount(0);
          setSelectedInvoiceBalanceAmount(0);
        }}
        dialogClassName="feePaymentModal"
        contentClassName="feePaymentModalContent"
        centered
      >
        <Modal.Header closeButton className="feePaymentModalHeader">
          <Modal.Title>{isKenyanStudent ? 'M-Pesa Payment' : 'Card Payment'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="feePaymentModalBody">
          <div className="feePaymentAmount">
            <span>Total to Pay</span>
            <strong>
              {(amountToPay * conversionRate).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} {currentCurrencyCode}
            </strong>
            <small>{selectedMonth}</small>
          </div>

          {selectedPaymentType === "invoice" && (
            <>
              <div className="feePaymentField">
                <label htmlFor="customInvoiceAmount">Amount to pay</label>
                <input
                  id="customInvoiceAmount"
                  type="number"
                  className="form-control"
                  value={customPaymentAmount}
                  min={convertKesToDisplayAmount(invoicePaymentBounds.minKes)}
                  max={convertKesToDisplayAmount(invoicePaymentBounds.maxKes)}
                  step={currentCurrencyCode === 'USD' ? '0.01' : '1'}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setCustomPaymentAmount(nextValue);
                    const nextAmountKes = convertDisplayAmountToKes(nextValue);
                    if (nextAmountKes > 0) {
                      setAmountToPay(nextAmountKes);
                    }
                  }}
                  placeholder={`Enter amount in ${currentCurrencyCode}`}
                />
                <div className="feePaymentRange">
                  <span>Minimum: {minimumInvoicePaymentLabel}</span>
                  <span>Maximum: {maximumInvoicePaymentLabel}</span>
                </div>
                <p className="feePaymentHint">
                  Partial payments are allowed. The minimum is the lower of the invoice balance or 80% of the invoice amount, with a floor of KES 1,000. The maximum is your total remaining fee balance.
                </p>
              </div>
            </>
          )}

          {isKenyanStudent ? (
            <div className="feePaymentField">
              <label htmlFor="mpesaNumber">M-Pesa Phone Number</label>
              <input
                id="mpesaNumber"
                type="number"
                className="form-control"
                ref={mpesaNumber}
                placeholder="Enter your M-Pesa number"
              />
              <p className="feePaymentHint">We’ll send a prompt to your phone to confirm payment.</p>
            </div>
          ) : (
            <div className="feePaymentField">
              <p className="feePaymentHint">International payments are processed securely in USD via Paystack.</p>
            </div>
          )}

          {paymentStatusText && (
            <p className="feePaymentStatus">{paymentStatusText}</p>
          )}

        </Modal.Body>
        <Modal.Footer className="feePaymentModalFooter">
          <Button variant="secondary" onClick={() => {
            setAmountToPay(0);
            setSelectedMonth("");
            setCustomPaymentAmount("");
            setSelectedInvoiceExpectedAmount(0);
            setSelectedInvoiceBalanceAmount(0);
          }}>
            Cancel
          </Button>

          {isLoading ? (
            <Button variant="primary" disabled>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              Processing...
            </Button>
          ) : (
            <Button variant="primary" onClick={handleProceedToPayment}>
              {isKenyanStudent ? 'Send Prompt' : 'Pay with Paystack'}
            </Button>
          )}

        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Bounce}
      />

    </div>
  );
}

export default InvoicesComp;
